import type { IEItem, IEMBuyProgramCatalogItem } from '@/services/iem';

/**
 * Builds the Buy Program fragment for a `completeIssue` request.
 *
 * Both `hasBuyProgramItems` and `buyProgramAssetIds` must travel together:
 * - `buyProgramAssetIds` tells the server which freshly-created assets should
 *   bypass the cart reservation check.
 * - `hasBuyProgramItems` signals Buy Program context and guards the edge case
 *   where the bypass ID list is empty (server skips validation entirely).
 *
 * The fragment is always sent when buy program assets were pre-created,
 * regardless of whether an SF 1034 document payload is present (e.g., restricted
 * buy program skip path).
 */
export const buildBuyProgramRequestFragment = (
  buyProgramAssetIds: string[],
): { hasBuyProgramItems: true; buyProgramAssetIds: string[] } | Record<string, never> => {
  if (buyProgramAssetIds.length === 0) return {};
  return { hasBuyProgramItems: true, buyProgramAssetIds };
};

/**
 * Extracts catalog IDs from IE Kit items into a Set for efficient lookup.
 */
export const extractAuthorizedCatalogIds = (ieItems: IEItem[]): Set<string> => {
  const ids = new Set<string>();
  ieItems.forEach((item) => {
    if (item.catalogId) {
      ids.add(item.catalogId);
    }
  });
  return ids;
};

/**
 * Filters Buy Program catalog items to only include those authorized via IE Kit.
 */
export const filterAuthorizedBuyProgramItems = (
  buyProgramItems: IEMBuyProgramCatalogItem[],
  authorizedCatalogIds: Set<string>,
): IEMBuyProgramCatalogItem[] => {
  if (authorizedCatalogIds.size === 0) return [];
  return buyProgramItems.filter((item) => authorizedCatalogIds.has(item.id));
};
